/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthenticationServiceApi, ConnectionProviderService } from '@api/authentication';
import { WorkspaceRepository } from '@database/central';
import { QueryParamsProvider } from '@domain/routing';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { LoadingIndicatorState, ToastDuration, ToastService } from '@shared';
import { firstValueFrom, Subscription, switchMap } from 'rxjs';
import { AuthProvider } from '../services';

@Component({
	selector: 'app-authentication-modal',
	templateUrl: './authentication-modal.component.html',
	styleUrls: ['./authentication-modal.component.scss']
})
export class AuthenticationModalComponent implements OnInit, OnDestroy {
	public usernameControl: UntypedFormControl;
	public passwordControl: UntypedFormControl;
	public authForm: UntypedFormGroup;
	public showPassword = false;
	private authSubscription: Subscription;

	public constructor(
		private modalCtrl: ModalController,
		private authenticationApi: AuthenticationServiceApi,
		private authProvider: AuthProvider,
		private toastService: ToastService,
		private translateService: TranslateService,
		private queryParamsProvider: QueryParamsProvider,
		private workspaceRepository: WorkspaceRepository,
		private loadingIndicator: LoadingIndicatorState,
		private connectionProviderService: ConnectionProviderService
	) {}

	public ngOnInit(): void {
		this.usernameControl = new UntypedFormControl('', [Validators.required]);
		this.passwordControl = new UntypedFormControl('', [Validators.required]);

		this.authForm = new UntypedFormGroup({
			usernameControl: this.usernameControl,
			passwordControl: this.passwordControl
		});
	}

	public toggleFieldTextType() {
		this.showPassword = !this.showPassword;
	}

	public async onSubmit(formValues: { usernameControl: string; passwordControl: string }): Promise<void> {
		if (!this.isFormValid()) {
			return;
		}

		this.loadingIndicator.setIsLoading(true);

		const authMethod = await this.authProvider.getAuthenticationMethod();

		this.authSubscription = this.workspaceRepository
			.getConnectionId$(this.queryParamsProvider.workfileId)
			.pipe(
				switchMap(async (connectionId: string | undefined) => {
					if (!connectionId) {
						connectionId = await this.connectionProviderService.get();
					}

					return firstValueFrom(
						this.authenticationApi.login(connectionId, {
							AuthenticationMethod: authMethod,
							Userid: formValues.usernameControl,
							Password: formValues.passwordControl
						})
					);
				})
			)
			.subscribe(
				(accessToken: string) => {
					// todo: validate if it's JWT token format
					this.loadingIndicator.setIsLoading(false);
					this.modalCtrl.dismiss(accessToken);
				},
				() => {
					this.loadingIndicator.setIsLoading(false);

					this.toastService.presentError(
						this.translateService.instant('login.errorMessages.auth'),
						ToastDuration.Fast
					);

					this.modalCtrl.dismiss();
				}
			);
	}

	public isFormValid(): boolean {
		return this.authForm.valid;
	}

	public ngOnDestroy(): void {
		this.authSubscription.unsubscribe();
	}
}
